import React from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../../contexts/StoreProvider';
import { ArrowLeft, X } from 'lucide-react';

const StoreLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, recentlyAdded, setRecentlyAdded, getCartTotal } = useStore();

  const cartItemsCount = cart.reduce((total, item) => total + item.quantity, 0);

  const handleBack = () => {
    if (location.pathname === '/store' || location.pathname === '/store/') {
      navigate('/');
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-[#EBE6DD] flex flex-col text-[#13110E] font-sans selection:bg-[#13110E] selection:text-[#EBE6DD]">
      {/* Store Navigation Bar */}
      <nav className="border-b border-[#13110E]/10 sticky top-0 z-50 bg-[#EBE6DD]/90 backdrop-blur-sm">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12 py-4">
          <div className="flex justify-between items-center">
            
            {/* Left side: Back Button & Logo */}
            <div className="flex items-center gap-6">
              <button 
                onClick={handleBack}
                className="w-8 h-8 flex items-center justify-center border border-[#13110E]/20 hover:border-[#13110E] transition-colors"
                title="Back"
              >
                <ArrowLeft size={14} strokeWidth={1.5} className="text-[#13110E]" />
              </button>
              <Link to="/store" className="flex items-center">
                <img src="/logo.png" alt="Logo" className="h-6 object-contain" />
              </Link>
            </div>
            
            {/* Right side: Minimalist Links */}
            <div className="flex items-center space-x-8 text-[9px] tracking-[0.25em] uppercase font-medium">
              <Link to="/store/favorites" className="hover:opacity-60 transition-opacity hidden sm:block">
                SAVED
              </Link>
              <Link to="/store/orders" className="hover:opacity-60 transition-opacity hidden sm:block">
                LISTS
              </Link>
              <Link to="/store/cart" className="flex items-center gap-3 hover:opacity-60 transition-opacity">
                <span>BAG</span>
                <span className="bg-[#13110E] text-[#EBE6DD] rounded-full h-5 w-5 flex items-center justify-center text-[9px] tracking-normal font-bold">
                  {cartItemsCount}
                </span>
              </Link>
            </div>

          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 w-full max-w-[1400px] mx-auto px-6 sm:px-12 py-10 md:py-16">
        <Outlet />
      </main>

      {/* Floating Cart Banner */}
      {recentlyAdded && (
        <div className="fixed bottom-6 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-[450px] bg-[#1a1a1a] text-white p-3 rounded-2xl flex items-center shadow-2xl z-[100] animate-in slide-in-from-bottom-5 fade-in duration-300">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0 mr-3">
            {recentlyAdded.product.images && recentlyAdded.product.images.length > 0 ? (
              <img 
                src={recentlyAdded.product.images[0].image} 
                alt="Product" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs text-gray-500">No img</div>
            )}
          </div>
          <div className="flex-1 min-w-0 mr-3">
            <p className="text-sm font-medium line-clamp-1 text-[#f5f5f7]">{recentlyAdded.product.name}</p>
            <p className="text-xs text-gray-400">
              {cartItemsCount} item{cartItemsCount !== 1 ? 's' : ''} • ₵{getCartTotal().toFixed(2)}
            </p>
          </div>
          <Link 
            to="/store/cart" 
            className="bg-[#007AFF] text-white px-4 py-2 rounded-full text-[10px] font-bold tracking-wide mr-2 hover:bg-[#0066cc] transition-colors flex-shrink-0"
            onClick={() => setRecentlyAdded(null)}
          >
            VIEW CART
          </Link>
          <button 
            onClick={() => setRecentlyAdded(null)} 
            className="text-gray-400 hover:text-white transition-colors p-1 flex-shrink-0"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>
      )}
    </div>
  );
};

export default StoreLayout;
